import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Calculator, Clock, ArrowRight, TrendingDown, Shield, Zap,
  DollarSign, Brain, Rocket, Route, Sparkles, Coins, Globe, Heart,
  Plus, X, Link2, Copy, Download, Check,
} from 'lucide-react';

/**
 * ROI Calculator - World-class GPU savings calculator
 *
 * Design principles:
 * - Annual savings is the HERO number (what a CFO sees first)
 * - Visual before/after bars (CEO scans visually)
 * - Payback period front and center
 * - Forecaster cost included for honest math
 * - Outcome-focused breakdown (capabilities, not cache hit rates)
 * - Multi-GPU fleet input with sliders
 * - Shareable via URL, clipboard, or PDF
 */

// ============================================================
// Constants
// ============================================================

const ACCELERATOR_PRICING: Record<string, { price: number; category: string }> = {
    'Vera Rubin': { price: 10.00, category: 'flagship' },
    'GB200 NVL': { price: 8.00, category: 'flagship' },
    'B200 192GB': { price: 6.50, category: 'flagship' },
    'B100 192GB': { price: 5.00, category: 'flagship' },
    'H200 141GB': { price: 4.50, category: 'premium' },
    'H100 80GB': { price: 3.00, category: 'premium' },
    'A100 80GB': { price: 2.50, category: 'standard' },
    'A100 40GB': { price: 2.10, category: 'standard' },
    'A40 48GB': { price: 1.00, category: 'standard' },
    'L40S 48GB': { price: 1.20, category: 'standard' },
    'L4 24GB': { price: 0.40, category: 'entry' },
    'T4 16GB': { price: 0.35, category: 'entry' },
    'MI300X 192GB': { price: 3.50, category: 'premium' },
    'MI250X 128GB': { price: 2.80, category: 'standard' },
    'Inferentia2': { price: 0.99, category: 'accelerator' },
    'Trainium': { price: 1.34, category: 'accelerator' },
    'TPU v5e': { price: 1.20, category: 'accelerator' },
    'TPU v4': { price: 2.40, category: 'accelerator' },
    'Gaudi2': { price: 1.50, category: 'accelerator' },
    'Gaudi3': { price: 2.80, category: 'accelerator' },
};

type WorkloadProfile = 'variable' | 'steady' | 'bursty' | 'batch';

const WORKLOAD_PROFILES: Record<WorkloadProfile, { label: string; description: string }> = {
    variable: { label: 'Variable Traffic', description: 'Peaks and valleys throughout the day' },
    steady: { label: 'Steady State', description: 'Consistent load with minimal variance' },
    bursty: { label: 'Bursty/Spiky', description: 'Unpredictable spikes, long idle periods' },
    batch: { label: 'Batch Processing', description: 'Scheduled jobs, overnight processing' },
};

// Optimization layers — drives internal calculation, outcomes shown as capability badges
interface OptimizationLayer {
    id: string;
    name: string;
    icon: React.ElementType;
    outcome: string;
    savingsRange: { min: number; max: number };
    applicability: Record<WorkloadProfile, number>;
}

const OPTIMIZATION_LAYERS: OptimizationLayer[] = [
    {
        id: 'demand-forecasting',
        name: 'Predictive Scaling',
        icon: Brain,
        outcome: 'Scale ahead of demand, not behind it. Eliminate over-provisioned headroom.',
        savingsRange: { min: 0.10, max: 0.20 },
        applicability: { variable: 1.0, steady: 0.3, bursty: 0.9, batch: 0.5 },
    },
    {
        id: 'instant-startup',
        name: 'Zero Cold Starts',
        icon: Rocket,
        outcome: 'GPUs ready before traffic arrives. No idle capacity reserved as cold-start buffer.',
        savingsRange: { min: 0.03, max: 0.08 },
        applicability: { variable: 0.8, steady: 0.4, bursty: 1.0, batch: 0.6 },
    },
    {
        id: 'intelligent-routing',
        name: 'Intelligent Routing',
        icon: Route,
        outcome: 'Every request placed on the optimal GPU. Higher utilization from the same fleet.',
        savingsRange: { min: 0.08, max: 0.15 },
        applicability: { variable: 0.9, steady: 1.0, bursty: 0.7, batch: 0.8 },
    },
    {
        id: 'accelerated-generation',
        name: 'Faster Inference',
        icon: Sparkles,
        outcome: 'More output per GPU-hour through smarter batching and memory management.',
        savingsRange: { min: 0.05, max: 0.12 },
        applicability: { variable: 0.8, steady: 0.9, bursty: 0.6, batch: 1.0 },
    },
    {
        id: 'gpu-hibernation',
        name: 'GPU Hibernation',
        icon: Shield,
        outcome: 'Idle GPUs suspended automatically. State preserved, wake in seconds when demand returns.',
        savingsRange: { min: 0.05, max: 0.15 },
        applicability: { variable: 0.9, steady: 0.3, bursty: 1.0, batch: 0.8 },
    },
    {
        id: 'dynamic-cost',
        name: 'Cost Arbitrage',
        icon: Coins,
        outcome: 'Route to cheapest available compute across your provider mix. Spot when safe, on-demand when not.',
        savingsRange: { min: 0.15, max: 0.30 },
        applicability: { variable: 0.7, steady: 0.9, bursty: 0.5, batch: 1.0 },
    },
    {
        id: 'smart-data-routing',
        name: 'Network Optimization',
        icon: Globe,
        outcome: 'Minimize cross-region transfer costs. Data stays close to compute.',
        savingsRange: { min: 0.02, max: 0.05 },
        applicability: { variable: 0.6, steady: 0.7, bursty: 0.4, batch: 0.8 },
    },
    {
        id: 'self-healing',
        name: 'Auto-Recovery',
        icon: Heart,
        outcome: 'Failures recovered in seconds, not minutes. No wasted GPU-hours from outages.',
        savingsRange: { min: 0.02, max: 0.05 },
        applicability: { variable: 0.7, steady: 0.8, bursty: 0.9, batch: 0.5 },
    },
];

// ============================================================
// Helpers
// ============================================================

function getForecasterMonthlyCost(baselineMonthlyCost: number): number {
    return baselineMonthlyCost * 0.06;
}

function getForecasterTier(_baselineMonthlyCost: number): string {
    return '6%';
}

function calculateCombinedSavings(
    layers: OptimizationLayer[],
    workload: WorkloadProfile,
    spotEligible: boolean
): { min: number; max: number } {
    let remainingCost = 1.0;

    for (const layer of layers) {
        if (layer.id === 'dynamic-cost' && !spotEligible) continue;
        const applicability = layer.applicability[workload];
        const avgSavings = ((layer.savingsRange.min + layer.savingsRange.max) / 2) * applicability;
        remainingCost *= (1 - avgSavings);
    }

    const totalSavings = 1 - remainingCost;
    const cappedMax = Math.min(totalSavings * 1.1, 0.65);
    const cappedMin = Math.max(totalSavings * 0.7, 0.15);

    return { min: cappedMin, max: cappedMax };
}

// ============================================================
// Fleet type & URL sharing
// ============================================================

interface FleetRow {
    type: string;
    count: number;
}

function parseURLParams(): { fleet: FleetRow[]; hours: number; workload: WorkloadProfile; spot: boolean } | null {
    try {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('g0')) return null;

        const fleet: FleetRow[] = [];
        for (let i = 0; i < 6; i++) {
            const type = params.get(`g${i}`);
            const count = params.get(`c${i}`);
            if (type && count && ACCELERATOR_PRICING[type]) {
                fleet.push({ type, count: Math.max(1, parseInt(count) || 1) });
            }
        }
        if (fleet.length === 0) return null;

        const hours = Math.max(1, Math.min(24, parseInt(params.get('h') || '24') || 24));
        const workload = (params.get('w') || 'variable') as WorkloadProfile;
        const spot = params.get('s') !== '0';

        return { fleet, hours, workload: WORKLOAD_PROFILES[workload] ? workload : 'variable', spot };
    } catch {
        return null;
    }
}

function buildShareURL(fleet: FleetRow[], hours: number, workload: WorkloadProfile, spot: boolean): string {
    const params = new URLSearchParams();
    fleet.forEach((row, i) => {
        params.set(`g${i}`, row.type);
        params.set(`c${i}`, String(row.count));
    });
    params.set('h', String(hours));
    params.set('w', workload);
    params.set('s', spot ? '1' : '0');

    const url = new URL(window.location.href);
    url.search = params.toString();
    url.hash = 'roi';
    return url.toString();
}

// ============================================================
// Hooks
// ============================================================

function useAnimatedNumber(target: number, duration = 600): number {
    const [display, setDisplay] = useState(target);
    const prevRef = useRef(target);
    const rafRef = useRef<number>(0);
    const initialRef = useRef(true);

    useEffect(() => {
        if (initialRef.current) {
            initialRef.current = false;
            setDisplay(target);
            prevRef.current = target;
            return;
        }

        const start = prevRef.current;
        const diff = target - start;
        if (Math.abs(diff) < 1) {
            setDisplay(target);
            prevRef.current = target;
            return;
        }

        const startTime = performance.now();
        const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            setDisplay(start + diff * eased);
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(animate);
            } else {
                setDisplay(target);
                prevRef.current = target;
            }
        };

        rafRef.current = requestAnimationFrame(animate);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            prevRef.current = target;
        };
    }, [target, duration]);

    return display;
}

// ============================================================
// Sub-components
// ============================================================

const SLIDER_CSS = `
.roi-slider{-webkit-appearance:none;appearance:none;height:8px;border-radius:9999px;outline:none}
.roi-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:22px;height:22px;border-radius:50%;background:#fff;cursor:pointer;box-shadow:0 0 0 3px rgba(132,204,22,0.3),0 2px 6px rgba(0,0,0,0.3)}
.roi-slider::-moz-range-thumb{width:22px;height:22px;border-radius:50%;background:#fff;cursor:pointer;border:none;box-shadow:0 0 0 3px rgba(132,204,22,0.3),0 2px 6px rgba(0,0,0,0.3)}
`;

const RangeSlider = ({ value, min, max, onChange, label }: {
    value: number;
    min: number;
    max: number;
    onChange: (v: number) => void;
    label?: string;
}) => {
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(String(value));
    const pct = ((value - min) / (max - min)) * 100;

    const handleSubmit = () => {
        const num = parseInt(editValue);
        if (!isNaN(num)) onChange(Math.max(min, Math.min(max, num)));
        setEditing(false);
    };

    return (
        <div>
            {label && (
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">{label}</span>
                    {editing ? (
                        <input
                            type="number"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onBlur={handleSubmit}
                            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                            autoFocus
                            className="w-24 bg-white/10 border border-volt-500/50 rounded px-2 py-1 text-white text-right text-lg font-bold focus:outline-none"
                        />
                    ) : (
                        <button
                            onClick={() => { setEditValue(String(value)); setEditing(true); }}
                            className="text-xl font-bold text-white hover:text-volt-400 transition-colors cursor-text tabular-nums"
                            title="Click to type exact value"
                        >
                            {value.toLocaleString()}
                        </button>
                    )}
                </div>
            )}
            <input
                type="range"
                min={min}
                max={max}
                value={value}
                onChange={e => onChange(parseInt(e.target.value))}
                className="roi-slider w-full cursor-pointer"
                style={{
                    background: `linear-gradient(to right, #84cc16 0%, #a3e635 ${pct}%, rgba(255,255,255,0.08) ${pct}%, rgba(255,255,255,0.08) 100%)`
                }}
            />
        </div>
    );
};

const SavingsBar = ({ label, amount, maxAmount, color, animate }: {
    label: string;
    amount: number;
    maxAmount: number;
    color: 'default' | 'savings';
    animate?: boolean;
}) => {
    const pct = maxAmount > 0 ? (amount / maxAmount) * 100 : 0;
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">{label}</span>
                <span className={`text-sm font-semibold tabular-nums ${color === 'savings' ? 'text-volt-400' : 'text-white'}`}>
                    ${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}/mo
                </span>
            </div>
            <div className="h-10 rounded-lg bg-white/5 overflow-hidden">
                <div
                    className={`h-full rounded-lg ${
                        color === 'savings'
                            ? 'bg-gradient-to-r from-volt-500/60 to-volt-400/60'
                            : 'bg-gradient-to-r from-white/20 to-white/10'
                    } ${animate ? 'transition-all duration-700 ease-out' : ''}`}
                    style={{ width: `${Math.max(pct, 2)}%` }}
                />
            </div>
        </div>
    );
};

// ============================================================
// Main Component
// ============================================================

export const ROICalculator = () => {
    const [fleet, setFleet] = useState<FleetRow[]>([{ type: 'H100 80GB', count: 64 }]);
    const [hoursPerDay, setHoursPerDay] = useState(24);
    const [workloadProfile, setWorkloadProfile] = useState<WorkloadProfile>('variable');
    const [spotEligible, setSpotEligible] = useState(true);
    const [toast, setToast] = useState<string | null>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

    // Restore from URL params on mount
    useEffect(() => {
        const parsed = parseURLParams();
        if (parsed) {
            setFleet(parsed.fleet);
            setHoursPerDay(parsed.hours);
            setWorkloadProfile(parsed.workload);
            setSpotEligible(parsed.spot);
        }
    }, []);

    // Toast auto-dismiss
    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 2500);
            return () => clearTimeout(t);
        }
    }, [toast]);

    // Calculation
    const calc = useMemo(() => {
        const monthlyHours = hoursPerDay * 30;
        const baselineMonthlyCost = fleet.reduce((sum, row) => {
            const price = ACCELERATOR_PRICING[row.type]?.price ?? 3.0;
            return sum + row.count * price * monthlyHours;
        }, 0);
        const totalGPUs = fleet.reduce((sum, row) => sum + row.count, 0);
        const blendedHourly = totalGPUs > 0 ? baselineMonthlyCost / (totalGPUs * monthlyHours) : 0;

        const savings = calculateCombinedSavings(OPTIMIZATION_LAYERS, workloadProfile, spotEligible);
        const avgSavingsRate = (savings.min + savings.max) / 2;
        const monthlySavingsAvg = baselineMonthlyCost * avgSavingsRate;
        const forecasterCost = getForecasterMonthlyCost(baselineMonthlyCost);
        const forecasterTier = getForecasterTier(baselineMonthlyCost);
        const netMonthlySavings = monthlySavingsAvg - forecasterCost;
        const optimizedMonthly = baselineMonthlyCost - monthlySavingsAvg + forecasterCost;
        const paybackDays = netMonthlySavings > 0
            ? Math.max(1, Math.ceil((forecasterCost / monthlySavingsAvg) * 30))
            : null;
        const roiMultiple = forecasterCost > 0 ? monthlySavingsAvg / forecasterCost : 0;

        return {
            baselineMonthlyCost,
            avgSavingsRate,
            monthlySavingsAvg,
            forecasterCost,
            forecasterTier,
            netMonthlySavings,
            optimizedMonthly,
            annualNetSavings: netMonthlySavings * 12,
            paybackDays,
            roiMultiple,
            totalGPUs,
            blendedHourly,
        };
    }, [fleet, hoursPerDay, workloadProfile, spotEligible]);

    // Animated values (hero numbers + summary cards)
    const animAnnual = useAnimatedNumber(calc.annualNetSavings);
    const animMonthly = useAnimatedNumber(calc.netMonthlySavings);
    const animPayback = useAnimatedNumber(calc.paybackDays ?? 0);
    const animROI = useAnimatedNumber(calc.roiMultiple);
    const animBaseline = useAnimatedNumber(calc.baselineMonthlyCost);
    const animForecaster = useAnimatedNumber(calc.forecasterCost);

    const fmt = (n: number) => Math.round(n).toLocaleString('en-US');

    // Fleet management
    const addFleetRow = () => {
        if (fleet.length >= 6) return;
        const usedTypes = new Set(fleet.map(r => r.type));
        const available = Object.keys(ACCELERATOR_PRICING).find(t => !usedTypes.has(t));
        if (available) setFleet([...fleet, { type: available, count: 16 }]);
    };

    const removeFleetRow = (index: number) => {
        if (fleet.length <= 1) return;
        setFleet(fleet.filter((_, i) => i !== index));
    };

    const updateFleetRow = (index: number, update: Partial<FleetRow>) => {
        setFleet(fleet.map((row, i) => i === index ? { ...row, ...update } : row));
    };

    // Export: share URL
    const copyShareURL = async () => {
        const url = buildShareURL(fleet, hoursPerDay, workloadProfile, spotEligible);
        await navigator.clipboard.writeText(url);
        setToast('Link copied to clipboard');
    };

    // Export: copy summary
    const copyResults = async () => {
        const fleetDesc = fleet.map(r => `  ${r.count}x ${r.type}`).join('\n');
        const summary = [
            'Forecaster ROI Estimate',
            '',
            'Fleet:',
            fleetDesc,
            `Total GPUs: ${calc.totalGPUs}`,
            `Hours/Day: ${hoursPerDay}`,
            `Traffic: ${WORKLOAD_PROFILES[workloadProfile].label}`,
            `Spot eligible: ${spotEligible ? 'Yes' : 'No'}`,
            '',
            `Current monthly spend: $${fmt(calc.baselineMonthlyCost)}`,
            `Projected annual savings: $${fmt(calc.annualNetSavings)}`,
            `Net monthly savings: $${fmt(calc.netMonthlySavings)}`,
            `Forecaster cost: $${fmt(calc.forecasterCost)}/mo (${calc.forecasterTier} of spend)`,
            `Payback period: ${calc.paybackDays ? `${calc.paybackDays} days` : 'N/A'}`,
            `ROI: ${calc.roiMultiple.toFixed(1)}x return`,
            '',
            'Learn more: forecaster.ai',
        ].join('\n');
        await navigator.clipboard.writeText(summary);
        setToast('Results copied to clipboard');
    };

    // Export: PDF download
    const downloadPDF = async () => {
        if (!resultsRef.current) return;
        setToast('Generating PDF...');
        try {
            const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
                import('jspdf'),
                import('html2canvas'),
            ]);
            const canvas = await html2canvas(resultsRef.current, {
                backgroundColor: '#0a0a0b',
                scale: 2,
            });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: [canvas.width / 2, canvas.height / 2],
            });
            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
            pdf.save('forecaster-roi-estimate.pdf');
            setToast('PDF downloaded');
        } catch {
            setToast('PDF generation failed');
        }
    };

    return (
        <section className="py-24 lg:py-32 bg-gradient-to-b from-obsidian-900 to-obsidian-800">
            <style>{SLIDER_CSS}</style>
            <div className="max-w-6xl mx-auto px-6">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-volt-500/10 border border-volt-500/20 text-volt-400 mb-6 text-sm font-medium">
                        <Calculator className="w-4 h-4" />
                        <span>ROI Calculator</span>
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">
                        How much will you save?
                    </h2>
                    <p className="text-gray-400 max-w-2xl mx-auto">
                        Configure your GPU fleet. See the math. Forecaster cost included — no hidden fees.
                    </p>
                </div>

                {/* ===== RESULTS (ref for PDF capture) ===== */}
                <div ref={resultsRef}>
                    {/* Hero: Annual Net Savings */}
                    <div className="relative mb-12 p-8 md:p-10 rounded-2xl bg-gradient-to-br from-volt-500/10 via-volt-500/5 to-transparent border border-volt-500/30 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-volt-500/5 via-transparent to-transparent rounded-2xl" />
                        <div className="relative grid md:grid-cols-3 gap-8 items-center">
                            <div className="md:col-span-2 text-center md:text-left">
                                <div className="text-sm text-volt-400 font-medium mb-2">Projected Annual Net Savings</div>
                                <div className="text-5xl md:text-6xl font-bold text-white mb-2 tabular-nums">
                                    ${fmt(animAnnual)}
                                    <span className="text-xl text-gray-400 font-normal">/year</span>
                                </div>
                                <div className="text-gray-400">
                                    After Forecaster cost of ${fmt(animForecaster)}/mo ({calc.forecasterTier} of spend)
                                </div>
                            </div>
                            <div className="flex flex-col gap-4">
                                {calc.paybackDays && (
                                    <div className="p-4 rounded-xl bg-obsidian-900/60 border border-white/10 text-center">
                                        <Clock className="w-5 h-5 text-volt-400 mx-auto mb-2" />
                                        <div className="text-2xl font-bold text-white tabular-nums">{Math.round(animPayback)} days</div>
                                        <div className="text-xs text-gray-500">Payback period</div>
                                    </div>
                                )}
                                <div className="p-4 rounded-xl bg-obsidian-900/60 border border-white/10 text-center">
                                    <TrendingDown className="w-5 h-5 text-volt-400 mx-auto mb-2" />
                                    <div className="text-2xl font-bold text-white tabular-nums">{animROI.toFixed(1)}x</div>
                                    <div className="text-xs text-gray-500">Return on Forecaster spend</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Visual Bars */}
                    <div className="mb-12 p-6 rounded-xl bg-white/[0.02] border border-white/10">
                        <div className="space-y-6">
                            <SavingsBar
                                label="Current GPU spend"
                                amount={calc.baselineMonthlyCost}
                                maxAmount={calc.baselineMonthlyCost}
                                color="default"
                                animate
                            />
                            <SavingsBar
                                label="With Forecaster"
                                amount={calc.optimizedMonthly}
                                maxAmount={calc.baselineMonthlyCost}
                                color="savings"
                                animate
                            />
                        </div>
                        <div className="mt-6 flex items-center justify-between pt-4 border-t border-white/10">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-volt-500" />
                                <span className="text-sm text-gray-400">
                                    {(calc.avgSavingsRate * 100).toFixed(0)}% reduction
                                </span>
                            </div>
                            <div className="text-lg font-semibold text-volt-400 tabular-nums">
                                ${fmt(animMonthly)} saved/mo
                            </div>
                        </div>
                    </div>

                    {/* Cost Summary Cards */}
                    <div className="grid md:grid-cols-3 gap-4 mb-10">
                        <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                            <DollarSign className="w-5 h-5 text-gray-500 mb-3" />
                            <div className="text-sm text-gray-500 mb-1">Current Monthly</div>
                            <div className="text-3xl font-bold text-white tabular-nums">${fmt(animBaseline)}</div>
                        </div>
                        <div className="bg-white/[0.02] border border-white/10 rounded-xl p-6">
                            <Zap className="w-5 h-5 text-gray-500 mb-3" />
                            <div className="text-sm text-gray-500 mb-1">Forecaster Cost</div>
                            <div className="text-3xl font-bold text-white tabular-nums">${fmt(animForecaster)}</div>
                            <div className="text-xs text-gray-500 mt-1">{calc.forecasterTier} of managed spend</div>
                        </div>
                        <div className="bg-volt-500/10 border border-volt-500/30 rounded-xl p-6">
                            <TrendingDown className="w-5 h-5 text-volt-400 mb-3" />
                            <div className="text-sm text-volt-400 mb-1">Net Monthly Savings</div>
                            <div className="text-3xl font-bold text-white tabular-nums">${fmt(animMonthly)}</div>
                            <div className="text-xs text-volt-400 mt-1">After Forecaster cost</div>
                        </div>
                    </div>
                </div>

                {/* ===== FLEET INPUT ===== */}
                <div className="mb-6">
                    <div className="text-sm font-medium text-gray-400 mb-3 flex items-center justify-between">
                        <span>GPU Fleet ({calc.totalGPUs.toLocaleString()} GPUs &middot; ${calc.blendedHourly.toFixed(2)}/GPU-hr blended)</span>
                        {fleet.length < 6 && (
                            <button
                                onClick={addFleetRow}
                                className="inline-flex items-center gap-1 text-volt-400 hover:text-volt-300 transition-colors text-sm"
                            >
                                <Plus className="w-3.5 h-3.5" /> Add GPU type
                            </button>
                        )}
                    </div>
                    <div className="space-y-3">
                        {fleet.map((row, i) => (
                            <div key={i} className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
                                <div className="flex items-start gap-4">
                                    <div className="w-48 flex-shrink-0">
                                        <label className="block text-xs text-gray-500 mb-1.5">Accelerator</label>
                                        <select
                                            value={row.type}
                                            onChange={e => updateFleetRow(i, { type: e.target.value })}
                                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-volt-500/50"
                                        >
                                            {Object.entries(ACCELERATOR_PRICING).map(([type, { price }]) => (
                                                <option key={type} value={type} className="bg-obsidian-900">
                                                    {type} (${price}/hr)
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex-1">
                                        <RangeSlider
                                            label="GPU Count"
                                            value={row.count}
                                            min={1}
                                            max={2000}
                                            onChange={v => updateFleetRow(i, { count: v })}
                                        />
                                    </div>
                                    {fleet.length > 1 && (
                                        <button
                                            onClick={() => removeFleetRow(i)}
                                            className="mt-6 p-2 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                            title="Remove"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ===== HOURS + TRAFFIC PATTERN ===== */}
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
                        <RangeSlider
                            label="Hours/Day"
                            value={hoursPerDay}
                            min={1}
                            max={24}
                            onChange={setHoursPerDay}
                        />
                    </div>
                    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
                        <label className="block text-sm text-gray-400 mb-2">Traffic Pattern</label>
                        <select
                            value={workloadProfile}
                            onChange={e => setWorkloadProfile(e.target.value as WorkloadProfile)}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white font-medium focus:outline-none focus:ring-2 focus:ring-volt-500/50"
                        >
                            {Object.entries(WORKLOAD_PROFILES).map(([key, { label }]) => (
                                <option key={key} value={key} className="bg-obsidian-900">
                                    {label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Spot toggle */}
                <div className="flex items-center justify-center gap-3 mb-10">
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            checked={spotEligible}
                            onChange={e => setSpotEligible(e.target.checked)}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-white/10 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-volt-500/50 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-volt-500" />
                    </label>
                    <span className="text-sm text-gray-400">
                        Spot instances eligible (workload can tolerate interruptions)
                    </span>
                </div>

                {/* ===== OPTIMIZATION CAPABILITIES (no secret sauce) ===== */}
                <div className="mb-8">
                    <div className="text-sm font-medium text-gray-400 mb-3">Optimization capabilities included</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {OPTIMIZATION_LAYERS.map(layer => {
                            const isDisabled = layer.id === 'dynamic-cost' && !spotEligible;
                            return (
                                <div
                                    key={layer.id}
                                    className={`group p-3 rounded-lg border transition-all ${
                                        isDisabled
                                            ? 'bg-white/[0.01] border-white/5 opacity-40'
                                            : 'bg-white/[0.02] border-white/10 hover:border-volt-500/30'
                                    }`}
                                    title={layer.outcome}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                                            isDisabled ? 'bg-white/5' : 'bg-volt-500/10'
                                        }`}>
                                            <layer.icon className={`w-3.5 h-3.5 ${isDisabled ? 'text-gray-600' : 'text-volt-400'}`} />
                                        </div>
                                        <span className="text-xs font-medium text-white leading-tight">{layer.name}</span>
                                    </div>
                                    <p className="text-[11px] text-gray-500 leading-snug line-clamp-2">{layer.outcome}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ===== EXPORT BUTTONS ===== */}
                <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                    <button
                        onClick={copyShareURL}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-gray-300 hover:text-white hover:border-white/20 transition-colors"
                    >
                        <Link2 className="w-4 h-4" /> Copy Link
                    </button>
                    <button
                        onClick={copyResults}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-gray-300 hover:text-white hover:border-white/20 transition-colors"
                    >
                        <Copy className="w-4 h-4" /> Copy Results
                    </button>
                    <button
                        onClick={downloadPDF}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-gray-300 hover:text-white hover:border-white/20 transition-colors"
                    >
                        <Download className="w-4 h-4" /> Download PDF
                    </button>
                </div>

                {/* Disclaimer */}
                <div className="p-5 rounded-xl bg-white/[0.02] border border-white/10 mb-8">
                    <p className="text-sm text-gray-400 text-center">
                        <span className="text-gray-300 font-medium">Estimated GPU costs used for example.</span>{' '}
                        Actual costs vary by provider and region. Results vary by workload, provider mix, and traffic patterns.
                    </p>
                </div>

                {/* CTA */}
                <div className="text-center">
                    <a
                        href="/trial"
                        className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-volt-500 text-obsidian-900 rounded-lg font-semibold text-lg hover:bg-volt-400 transition-colors group"
                    >
                        Start Free Trial
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <p className="text-sm text-gray-500 mt-3">
                        Self-hosted &bull; No credit card &bull; 5-min setup
                    </p>
                </div>
            </div>

            {/* Toast notification */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-volt-500 text-obsidian-900 rounded-lg font-medium text-sm shadow-lg animate-fade-in">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}
        </section>
    );
};
